/**
 * Permission Constants
 *
 * This file contains all permission codes used in the application.
 * These match the permission_code values in the database.
 *
 * Benefits:
 * 1. Avoid typos in permission strings
 * 2. Get autocomplete in IDE
 * 3. Single source of truth
 * 4. Easy to refactor if permission codes change
 *
 * @see api/docs/PERMISSION_LIST.MD for full descriptions
 */
export const PERMISSIONS = {
  // ═══════════════════════════════════════════════════════════════════════════
  // PRODUCT MANAGEMENT
  // Covers: Products, OEM, Category, Client
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create products, OEM, Category, Client */
  PRODUCT_CREATE: "product_create",
  /** Permission to read products, OEM, Category, Client */
  PRODUCT_READ: "product_read",
  /** Permission to update products, OEM, Category, Client */
  PRODUCT_UPDATE: "product_update",
  /** Permission to delete products, OEM, Category, Client */
  PRODUCT_DELETE: "product_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // USER MANAGEMENT
  // Covers: Users, Roles, Permissions
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create users, roles and permissions */
  USERS_CREATE: "users_create",
  /** Permission to read users, roles and permissions */
  USERS_READ: "users_read",
  /** Permission to update users, roles and permissions */
  USERS_UPDATE: "users_update",
  /** Permission to delete users, roles and permissions */
  USERS_DELETE: "users_delete",
  /** Permission to view users, roles and permissions */
  USERS_VIEW: "users_view",

  // ═══════════════════════════════════════════════════════════════════════════
  // PO (PURCHASE ORDER) MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create POs */
  PO_CREATE: "po_create",
  /** Permission to read POs */
  PO_READ: "po_read",
  /** Permission to update POs */
  PO_UPDATE: "po_update",
  /** Permission to delete POs */
  PO_DELETE: "po_delete",
  /** Permission to view all POs */
  PO_READ_ALL: "po_read_all",
  /** Permission to view pricing of all POs (Admin only) */
  PO_PRICING_VIEW_ALL: "po_pricing_view_all",
  /** Permission to update Assign Dispatch To on POs */
  PO_UPDATE_ASSIGN_DISPATCH_TO: "can_update_assign_dispatch_to",
  /** Permission to update Assign Service To on POs */
  PO_UPDATE_ASSIGN_SERVICE_TO: "can_update_assign_service_to",

  // ═══════════════════════════════════════════════════════════════════════════
  // DISPATCH MANAGEMENT
  // Covers: Dispatch Details accordion only
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Dispatch details */
  DISPATCH_CREATE: "dispatch_create",
  /** Permission to read Dispatch details */
  DISPATCH_READ: "dispatch_read",
  /** Permission to update Dispatch details */
  DISPATCH_UPDATE: "dispatch_update",
  /** Permission to delete Dispatch details */
  DISPATCH_DELETE: "dispatch_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // DISPATCH DOCUMENT MANAGEMENT
  // Covers: Dispatch Document accordion
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Dispatch Documents */
  DISPATCH_DOCUMENT_CREATE: "dispatch_document_create",
  /** Permission to read Dispatch Documents */
  DISPATCH_DOCUMENT_READ: "dispatch_document_read",
  /** Permission to update Dispatch Documents */
  DISPATCH_DOCUMENT_UPDATE: "dispatch_document_update",
  /** Permission to delete Dispatch Documents */
  DISPATCH_DOCUMENT_DELETE: "dispatch_document_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // DELIVERY MANAGEMENT
  // Covers: Delivery Confirmation accordion
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Delivery Confirmations */
  DELIVERY_CREATE: "delivery_create",
  /** Permission to read Delivery Confirmations */
  DELIVERY_READ: "delivery_read",
  /** Permission to update Delivery Confirmations */
  DELIVERY_UPDATE: "delivery_update",
  /** Permission to delete Delivery Confirmations */
  DELIVERY_DELETE: "delivery_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // PRE-COMMISSIONING MANAGEMENT
  // Covers: Pre-Commissioning accordion
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Pre-Commissioning records */
  PRE_COMMISSIONING_CREATE: "pre_commissioning_create",
  /** Permission to read Pre-Commissioning records */
  PRE_COMMISSIONING_READ: "pre_commissioning_read",
  /** Permission to update Pre-Commissioning records */
  PRE_COMMISSIONING_UPDATE: "pre_commissioning_update",
  /** Permission to delete Pre-Commissioning records */
  PRE_COMMISSIONING_DELETE: "pre_commissioning_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // COMMISSIONING MANAGEMENT
  // Covers: Final Commissioning accordion only
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Final Commissioning records */
  COMMISSIONING_CREATE: "commissioning_create",
  /** Permission to read Final Commissioning records */
  COMMISSIONING_READ: "commissioning_read",
  /** Permission to update Final Commissioning records */
  COMMISSIONING_UPDATE: "commissioning_update",
  /** Permission to delete Final Commissioning records */
  COMMISSIONING_DELETE: "commissioning_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // WARRANTY MANAGEMENT
  // Covers: Warranty Certificate accordion
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Warranty Certificates */
  WARRANTY_CREATE: "warranty_create",
  /** Permission to read Warranty Certificates */
  WARRANTY_READ: "warranty_read",
  /** Permission to update Warranty Certificates */
  WARRANTY_UPDATE: "warranty_update",
  /** Permission to delete Warranty Certificates */
  WARRANTY_DELETE: "warranty_delete",

  // ═══════════════════════════════════════════════════════════════════════════
  // ACCOUNTING MANAGEMENT
  // Covers: Accounting accordion (Sales & Invoice, Payment, Dispatch/Commissioning Costs, Credit Notes)
  // ═══════════════════════════════════════════════════════════════════════════
  /** Permission to create Accounting entries */
  ACCOUNTING_CREATE: "accounting_create",
  /** Permission to read Accounting entries */
  ACCOUNTING_READ: "accounting_read",
  /** Permission to update Accounting entries */
  ACCOUNTING_UPDATE: "accounting_update",
  /** Permission to delete Accounting entries */
  ACCOUNTING_DELETE: "accounting_delete",
} as const;

/**
 * Type for permission values
 * Use this type when you need to accept a permission code as a parameter
 *
 * @example
 * function checkPermission(permission: Permission): boolean { ... }
 */
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ═══════════════════════════════════════════════════════════════════════════════
// PERMISSION GROUPS (for convenience)
// ═══════════════════════════════════════════════════════════════════════════════

/** All product management permissions */
export const PRODUCT_PERMISSIONS = [
  PERMISSIONS.PRODUCT_CREATE,
  PERMISSIONS.PRODUCT_READ,
  PERMISSIONS.PRODUCT_UPDATE,
  PERMISSIONS.PRODUCT_DELETE,
] as const;

/** All user management permissions */
export const USERS_PERMISSIONS = [
  PERMISSIONS.USERS_CREATE,
  PERMISSIONS.USERS_READ,
  PERMISSIONS.USERS_UPDATE,
  PERMISSIONS.USERS_DELETE,
  PERMISSIONS.USERS_VIEW,
] as const;

/** All PO management permissions */
export const PO_PERMISSIONS = [
  PERMISSIONS.PO_CREATE,
  PERMISSIONS.PO_READ,
  PERMISSIONS.PO_UPDATE,
  PERMISSIONS.PO_DELETE,
  PERMISSIONS.PO_READ_ALL,
  PERMISSIONS.PO_PRICING_VIEW_ALL,
  PERMISSIONS.PO_UPDATE_ASSIGN_DISPATCH_TO,
  PERMISSIONS.PO_UPDATE_ASSIGN_SERVICE_TO,
] as const;

/** All dispatch management permissions (Dispatch Details accordion) */
export const DISPATCH_PERMISSIONS = [
  PERMISSIONS.DISPATCH_CREATE,
  PERMISSIONS.DISPATCH_READ,
  PERMISSIONS.DISPATCH_UPDATE,
  PERMISSIONS.DISPATCH_DELETE,
] as const;

/** All dispatch document management permissions */
export const DISPATCH_DOCUMENT_PERMISSIONS = [
  PERMISSIONS.DISPATCH_DOCUMENT_CREATE,
  PERMISSIONS.DISPATCH_DOCUMENT_READ,
  PERMISSIONS.DISPATCH_DOCUMENT_UPDATE,
  PERMISSIONS.DISPATCH_DOCUMENT_DELETE,
] as const;

/** All delivery management permissions */
export const DELIVERY_PERMISSIONS = [
  PERMISSIONS.DELIVERY_CREATE,
  PERMISSIONS.DELIVERY_READ,
  PERMISSIONS.DELIVERY_UPDATE,
  PERMISSIONS.DELIVERY_DELETE,
] as const;

/** All pre-commissioning management permissions */
export const PRE_COMMISSIONING_PERMISSIONS = [
  PERMISSIONS.PRE_COMMISSIONING_CREATE,
  PERMISSIONS.PRE_COMMISSIONING_READ,
  PERMISSIONS.PRE_COMMISSIONING_UPDATE,
  PERMISSIONS.PRE_COMMISSIONING_DELETE,
] as const;

/** All commissioning management permissions (Final Commissioning accordion) */
export const COMMISSIONING_PERMISSIONS = [
  PERMISSIONS.COMMISSIONING_CREATE,
  PERMISSIONS.COMMISSIONING_READ,
  PERMISSIONS.COMMISSIONING_UPDATE,
  PERMISSIONS.COMMISSIONING_DELETE,
] as const;

/** All warranty management permissions */
export const WARRANTY_PERMISSIONS = [
  PERMISSIONS.WARRANTY_CREATE,
  PERMISSIONS.WARRANTY_READ,
  PERMISSIONS.WARRANTY_UPDATE,
  PERMISSIONS.WARRANTY_DELETE,
] as const;

/** All accounting management permissions */
export const ACCOUNTING_PERMISSIONS = [
  PERMISSIONS.ACCOUNTING_CREATE,
  PERMISSIONS.ACCOUNTING_READ,
  PERMISSIONS.ACCOUNTING_UPDATE,
  PERMISSIONS.ACCOUNTING_DELETE,
] as const;

